import React, { useEffect, useMemo, useState } from "react";
import { format, differenceInCalendarDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { DateRange } from "react-day-picker";
import {
  Archive,
  Calendar as CalendarIcon,
  Check,
  ChevronDown,
  Download,
  Edit2,
  Filter,
  Loader2,
  Mail,
  MessageCircle,
  Phone,
  Plus,
  Search,
  Upload,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetClose } from "@/components/ui/sheet";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  listObras,
  updateObra as cacheUpdateObra,
  deleteObra as cacheDeleteObra,
  saveObra as cacheSaveObra,
  getObraById,
  type ObraRecord,
} from "@/lib/obraCache";
import { ClienteCache, clienteDisplayName } from "@/services/clienteCache";
import { getClienteNomeById } from "@/utils/clientesData";
import { getCurrentUserName } from "@/utils/userUtils";

// Types
interface Endereco {
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
  cep?: string;
}

type Status = "em-planejamento" | "em-andamento" | "paralisada" | "concluida";

interface ContatoPF {
  id: string;
  nome: string;
  telefone?: string;
  email?: string;
}

interface Obra {
  id: string;
  codigo: string;
  nome: string;
  cliente: string; // PF ou PJ simplificado
  endereco: Endereco;
  dataInicio?: string; // ISO
  previsaoTermino?: string; // ISO
  responsavel?: string;
  status: Status;
  observacoes?: string;
  contatos?: ContatoPF[];
}

// Fonte única: obras reais do obraCache (Supabase). O shape local `Obra` é o
// formato de exibição desta tela; os mapeadores abaixo convertem de/para o
// ObraRecord persistido.
const statusToLocal = (s?: string): Status => {
  switch (s) {
    case "concluido": return "concluida";
    case "pausado":
    case "cancelado": return "paralisada";
    case "planejamento": return "em-planejamento";
    default: return "em-andamento";
  }
};
const statusToGlobal: Record<Status, string> = {
  "em-planejamento": "planejamento",
  "em-andamento": "em-andamento",
  paralisada: "pausado",
  concluida: "concluido",
};

const asIsoString = (d?: string | Date) =>
  d instanceof Date ? d.toISOString() : d || undefined;

function obraRecordToRow(o: ObraRecord): Obra {
  return {
    id: o.id,
    codigo: o.numeroObra || "",
    nome: o.nomeObra || "Obra sem nome",
    cliente: o.clienteObra ? getClienteNomeById(o.clienteObra) : "",
    endereco: {
      logradouro: o.endereco?.rua || undefined,
      numero: o.endereco?.numero || undefined,
      complemento: o.endereco?.complemento || undefined,
      cidade: o.endereco?.cidade || undefined,
      uf: o.endereco?.estado || undefined,
      cep: o.endereco?.cep || undefined,
    },
    dataInicio: asIsoString(o.dataInicio),
    previsaoTermino: asIsoString(o.previsaoTermino),
    responsavel: o.responsaveisObra?.[0] || undefined,
    status: statusToLocal(o.statusObra),
    observacoes: o.endereco?.observacao || "",
    contatos: [],
  };
}

// Cliente é exibido/editado pelo nome; ao salvar, resolve para o id real.
function resolveClienteId(nomeDisplay?: string): string | undefined {
  if (!nomeDisplay) return undefined;
  const c = ClienteCache.getClientes().find(
    (x) => clienteDisplayName(x) === nomeDisplay
  );
  return c?.id;
}

const DEFAULT_REPORT_DEFAULTS = {
  condicoesClimaticas: true, maoDeObra: true, equipamentos: true,
  atividades: true, ocorrencias: true, materiais: true,
  comentarios: true, fotos: true, videos: true, anexos: true,
};

// Helpers
function formatDate(date?: string) {
  if (!date) return "—";
  try {
    return format(new Date(date), "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return "—";
  }
}

function statusPill(status: Status) {
  const map: Record<Status, { label: string; className: string }> = {
    "em-planejamento": { label: "Em planejamento", className: "bg-muted text-foreground" },
    "em-andamento": { label: "Em andamento", className: "bg-primary/10 text-primary" },
    paralisada: { label: "Paralisada", className: "bg-warning/10 text-warning" },
    concluida: { label: "Concluída", className: "bg-success/10 text-success" },
  };
  const { label, className } = map[status];
  return <Badge className={cn("rounded-full", className)}>{label}</Badge>;
}

// MultiSelect using Popover + Command + Checkbox
type MultiSelectOption = { label: string; value: string };
function MultiSelect({
  options,
  values,
  onChange,
  placeholder,
  className,
}: {
  options: MultiSelectOption[];
  values: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className={cn("justify-between", className)}>
          <span className="truncate">
            {values.length ? `${values.length} selecionado(s)` : placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Filtrar..." />
          <CommandEmpty>Nenhuma opção</CommandEmpty>
          <CommandGroup>
            {options.map((opt) => {
              const checked = values.includes(opt.value);
              return (
                <CommandItem
                  key={opt.value}
                  onSelect={() => {
                    const next = checked
                      ? values.filter((v) => v !== opt.value)
                      : [...values, opt.value];
                    onChange(next);
                  }}
                >
                  <Checkbox checked={checked} className="mr-2" />
                  {opt.label}
                </CommandItem>
              );
            })}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Single autocomplete using Command
function Autocomplete({
  value,
  onChange,
  options,
  placeholder,
  className,
}: {
  value?: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className={cn("w-[280px] justify-between", className)}>
          <span className="truncate">{value || placeholder}</span>
          <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar..." />
          <CommandEmpty>Nenhum resultado</CommandEmpty>
          <CommandGroup>
            {options.map((opt) => (
              <CommandItem
                key={opt}
                onSelect={() => {
                  onChange(opt);
                  setOpen(false);
                }}
              >
                {opt}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Date range picker button
function DateRangePicker({ range, onChange }: { range: DateRange | undefined; onChange: (r: DateRange | undefined) => void }) {
  const [open, setOpen] = useState(false);
  const label = range?.from
    ? range.to
      ? `${format(range.from, "dd/MM/yyyy")} - ${format(range.to, "dd/MM/yyyy")}`
      : `${format(range.from, "dd/MM/yyyy")} - …`
    : "Período";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-[260px] justify-start text-left font-normal">
          <CalendarIcon className="mr-2 h-4 w-4" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 sm:w-[620px]" align="start">
        <Calendar
          mode="range"
          selected={range}
          onSelect={onChange}
          numberOfMonths={2}
          pagedNavigation
          fixedWeeks
          locale={ptBR}
          initialFocus
          className={cn("p-3 pointer-events-auto")}
        />
        <div className="flex justify-end gap-2 border-t p-2">
          <Button variant="ghost" size="sm" className="h-11 sm:h-9" onClick={() => onChange(undefined)} disabled={!range?.from && !range?.to}>
            Limpar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Work Modal (Create/Edit)
function WorkModal({ open, onOpenChange, obra, onSave }: { open: boolean; onOpenChange: (v: boolean) => void; obra?: Obra | null; onSave: (o: Obra) => void }) {
  const [form, setForm] = useState<Obra>(
    obra || {
      id: `${Date.now()}`,
      codigo: "",
      nome: "",
      cliente: "",
      endereco: {},
      status: "em-planejamento",
      dataInicio: undefined,
      previsaoTermino: undefined,
      responsavel: undefined,
      observacoes: "",
      contatos: [],
    }
  );
  const [vincularOpen, setVincularOpen] = useState(false);
  const [novoContato, setNovoContato] = useState<ContatoPF>({ id: "", nome: "" });

  useEffect(() => {
    if (obra) setForm(obra);
  }, [obra]);

  function handleSave() {
    // Validações
    if (!form.nome?.trim() || !form.codigo?.trim() || !form.cliente?.trim()) {
      toast({ title: "Preencha os campos obrigatórios", variant: "destructive" });
      return;
    }
    if (form.dataInicio && form.previsaoTermino) {
      const ini = new Date(form.dataInicio);
      const fim = new Date(form.previsaoTermino);
      if (ini.getTime() > fim.getTime()) {
        toast({ title: "Datas inválidas", description: "Início deve ser anterior ao término.", variant: "destructive" });
        return;
      }
    }
    onSave(form);
    onOpenChange(false);
    toast({ title: obra ? "Obra atualizada" : "Obra criada", description: form.nome });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-4">
        <DialogHeader>
          <DialogTitle>{obra ? "Editar Obra" : "Nova Obra"}</DialogTitle>
          <DialogDescription>Preencha os campos obrigatórios marcados com *</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm">Nome *</label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome da Obra" />
            </div>
            <div>
              <label className="text-sm">Código *</label>
              <Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} placeholder="Ex.: OB-1023" />
            </div>
          </div>

          <div className="col-span-2">
            <label className="text-sm">Cliente *</label>
            <Autocomplete value={form.cliente} onChange={(v) => setForm({ ...form, cliente: v })} options={ClienteCache.getClientes().map(clienteDisplayName)} placeholder="Selecione o cliente" className="w-full" />
            <div className="mt-1">
              <Button variant="secondary" size="sm" className="h-11 sm:h-9" onClick={() => setVincularOpen(true)}>
                + Adicionar contato do cliente
              </Button>
            </div>
          </div>

          <div>
            <label className="text-sm">Data Início *</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {form.dataInicio ? formatDate(form.dataInicio) : "Selecionar"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0" align="start">
                <Calendar
                  mode="single"
                  selected={form.dataInicio ? new Date(form.dataInicio) : undefined}
                  onSelect={(d) => setForm({ ...form, dataInicio: d ? d.toISOString() : undefined })}
                  locale={ptBR}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <label className="text-sm">Previsão de Término *</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {form.previsaoTermino ? formatDate(form.previsaoTermino) : "Selecionar"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0" align="start">
                <Calendar
                  mode="single"
                  selected={form.previsaoTermino ? new Date(form.previsaoTermino) : undefined}
                  onSelect={(d) => setForm({ ...form, previsaoTermino: d ? d.toISOString() : undefined })}
                  locale={ptBR}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <label className="text-sm">Responsável</label>
            <Autocomplete value={form.responsavel} onChange={(v) => setForm({ ...form, responsavel: v })} options={[getCurrentUserName()]} placeholder="Selecione o responsável" />
          </div>
          <div>
            <label className="text-sm">Status</label>
            <Autocomplete
              value={
                form.status === "em-planejamento"
                  ? "Em planejamento"
                  : form.status === "em-andamento"
                  ? "Em andamento"
                  : form.status === "paralisada"
                  ? "Paralisada"
                  : "Concluída"
              }
              onChange={(v) => {
                const map: Record<string, Status> = {
                  "Em planejamento": "em-planejamento",
                  "Em andamento": "em-andamento",
                  Paralisada: "paralisada",
                  "Concluída": "concluida",
                };
                setForm({ ...form, status: map[v] || "em-planejamento" });
              }}
              options={["Em planejamento", "Em andamento", "Paralisada", "Concluída"]}
              placeholder="Status"
            />
          </div>

          <div className="col-span-2 grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm">Logradouro</label>
              <Input value={form.endereco.logradouro || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, logradouro: e.target.value } })} />
            </div>
            <div>
              <label className="text-sm">Número</label>
              <Input value={form.endereco.numero || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, numero: e.target.value } })} />
            </div>
            <div>
              <label className="text-sm">Complemento</label>
              <Input value={form.endereco.complemento || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, complemento: e.target.value } })} />
            </div>
            <div>
              <label className="text-sm">Bairro</label>
              <Input value={form.endereco.bairro || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, bairro: e.target.value } })} />
            </div>
            <div>
              <label className="text-sm">Cidade</label>
              <Input value={form.endereco.cidade || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, cidade: e.target.value } })} />
            </div>
            <div>
              <label className="text-sm">UF</label>
              <Input value={form.endereco.uf || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, uf: e.target.value } })} />
            </div>
            <div className="col-span-2">
              <label className="text-sm">CEP</label>
              <Input value={form.endereco.cep || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, cep: e.target.value } })} />
            </div>
          </div>

          <div className="col-span-2">
            <label className="text-sm">Observações</label>
            <textarea
              className="w-full min-h-[96px] rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={form.observacoes || ""}
              onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
            />
          </div>

          {form.contatos && form.contatos.length > 0 && (
            <div className="col-span-2">
              <label className="text-sm">Contatos vinculados</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {form.contatos.map((c) => (
                  <span key={c.id} className="inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1 text-sm">
                    {c.nome}
                    {c.email && <Mail className="h-4 w-4" />}
                    {c.telefone && <MessageCircle className="h-4 w-4" />}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>Salvar</Button>
        </DialogFooter>

        {/* Vincular contato PF */}
        <Dialog open={vincularOpen} onOpenChange={setVincularOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Vincular Contato (PF)</DialogTitle>
              <DialogDescription>Busque e vincule um contato de Pessoa Física.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Autocomplete value={""} onChange={() => {}} options={["João da Silva", "Maria Oliveira", "Carlos Souza"]} placeholder="Buscar contato existente" className="w-full" />
              <div className="border-t pt-4">
                <p className="mb-2 text-sm">+ Novo contato (PF)</p>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Nome" value={novoContato.nome} onChange={(e) => setNovoContato({ ...novoContato, nome: e.target.value })} />
                  <Input placeholder="Telefone" value={novoContato.telefone || ""} onChange={(e) => setNovoContato({ ...novoContato, telefone: e.target.value })} />
                  <div className="col-span-2">
                    <Input placeholder="E-mail" value={novoContato.email || ""} onChange={(e) => setNovoContato({ ...novoContato, email: e.target.value })} />
                  </div>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      if (!novoContato.nome.trim()) return;
                      const contato: ContatoPF = { ...novoContato, id: `${Date.now()}` };
                      setForm({ ...form, contatos: [...(form.contatos || []), contato] });
                      setNovoContato({ id: "", nome: "" });
                      toast({ title: "Contato adicionado" });
                    }}
                  >
                    Adicionar contato
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Fechar</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

export default function Obras() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Obra[]>([]);

  // Filters state
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [clienteFilter, setClienteFilter] = useState<string | undefined>(undefined);
  const [respFilter, setRespFilter] = useState<string[]>([]);
  const [periodo, setPeriodo] = useState<DateRange | undefined>();

  // Modal & dialogs
  const [modalOpen, setModalOpen] = useState(false);
  const [editingObra, setEditingObra] = useState<Obra | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<Obra | null>(null);

  useEffect(() => {
    document.title = "Obras | Cadastro";
    // Fonte única: obras reais (obraCache hidratado do Supabase)
    setRows(listObras().map(obraRecordToRow));
    setLoading(false);
  }, []);

  const statusOptions: MultiSelectOption[] = [
    { label: "Em planejamento", value: "em-planejamento" },
    { label: "Em andamento", value: "em-andamento" },
    { label: "Paralisada", value: "paralisada" },
    { label: "Concluída", value: "concluida" },
  ];

  // Opções de filtro derivadas dos dados reais
  const respOptions: MultiSelectOption[] = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.responsavel).filter(Boolean) as string[]))
        .sort()
        .map((r) => ({ label: r, value: r })),
    [rows]
  );
  const clientesOptions = useMemo(
    () => ClienteCache.getClientes().map(clienteDisplayName),
    []
  );

  // Filtering
  const filtered = useMemo(() => {
    let data = [...rows];
    if (query) {
      const q = query.toLowerCase();
      data = data.filter((r) =>
        `${r.nome} ${r.codigo} ${r.cliente} ${r.responsavel} ${r.endereco.cidade || ""} ${r.endereco.uf || ""}`
          .toLowerCase()
          .includes(q)
      );
    }
    if (statusFilter.length) data = data.filter((r) => statusFilter.includes(r.status));
    if (clienteFilter) data = data.filter((r) => r.cliente === clienteFilter);
    if (respFilter.length) data = data.filter((r) => (r.responsavel ? respFilter.includes(r.responsavel) : false));
    if (periodo?.from) {
      const from = periodo.from.getTime();
      const to = periodo.to ? periodo.to.getTime() : from;
      data = data.filter((r) => {
        const di = r.dataInicio ? new Date(r.dataInicio).getTime() : 0;
        return di >= from && di <= to;
      });
    }
    return data;
  }, [rows, query, statusFilter, clienteFilter, respFilter, periodo]);

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 8;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page]);

  function addObra() {
    setEditingObra(null);
    setModalOpen(true);
  }

  function saveObra(obra: Obra) {
    // Persistência real via obraCache (write-through no Supabase)
    const record = getObraById(obra.id);
    const clienteId = resolveClienteId(obra.cliente);
    if (record) {
      cacheUpdateObra(obra.id, {
        nomeObra: obra.nome,
        numeroObra: obra.codigo,
        statusObra: statusToGlobal[obra.status],
        dataInicio: obra.dataInicio,
        previsaoTermino: obra.previsaoTermino,
        responsaveisObra: obra.responsavel ? [obra.responsavel] : [],
        clienteObra: clienteId ?? record.clienteObra,
        endereco: {
          ...record.endereco,
          rua: obra.endereco.logradouro ?? "",
          numero: obra.endereco.numero ?? "",
          complemento: obra.endereco.complemento ?? "",
          cidade: obra.endereco.cidade ?? "",
          estado: obra.endereco.uf ?? "",
          cep: obra.endereco.cep ?? "",
          observacao: obra.observacoes ?? "",
        },
      });
    } else {
      cacheSaveObra({
        nomeObra: obra.nome,
        numeroObra: obra.codigo,
        responsaveisObra: obra.responsavel ? [obra.responsavel] : [],
        clienteObra: clienteId ?? "",
        clientesResponsaveis: [],
        dataInicio: obra.dataInicio,
        previsaoTermino: obra.previsaoTermino,
        statusObra: statusToGlobal[obra.status],
        endereco: {
          rua: obra.endereco.logradouro ?? "",
          numero: obra.endereco.numero ?? "",
          complemento: obra.endereco.complemento ?? "",
          cidade: obra.endereco.cidade ?? "",
          estado: obra.endereco.uf ?? "",
          cep: obra.endereco.cep ?? "",
          referencia: "",
          observacao: obra.observacoes ?? "",
        },
        reportDefaults: DEFAULT_REPORT_DEFAULTS,
        aprovacao: { tipo: "simples", modo: "sem-hierarquia", participantes: [] },
      });
    }
    setRows(listObras().map(obraRecordToRow));
  }

  function archiveObra(obra: Obra) {
    cacheDeleteObra(obra.id);
    setRows(listObras().map(obraRecordToRow));
    toast({ title: "Obra arquivada", description: obra.nome });
  }

  // Inline editors
  function InlineStatus({ value, onChange }: { value: Status; onChange: (v: Status) => void }) {
    return (
      <Sheet>
        <SheetTrigger asChild>
          <button className="inline-flex min-h-11 items-center gap-2 rounded-md border border-input bg-background px-2 py-1 text-xs hover:bg-accent sm:min-h-8">
            {statusPill(value)}
            <ChevronDown className="h-3 w-3" />
          </button>
        </SheetTrigger>
        <SheetContent side="right" className="sm:max-w-sm">
          <SheetHeader>
            <SheetTitle>Alterar status</SheetTitle>
            <SheetDescription>Escolha o novo status da obra</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-1">
            {statusOptions.map((s) => {
              const selected = s.value === value;
              return (
                <SheetClose asChild key={s.value}>
                  <button
                    onClick={() => onChange(s.value as Status)}
                    className={cn(
                      "w-full text-left rounded-md px-3 py-2 text-sm hover:bg-accent",
                      selected && "bg-accent"
                    )}
                  >
                    <span className="inline-flex items-center gap-2">
                      {selected && <Check className="h-4 w-4" />}
                      {s.label}
                    </span>
                  </button>
                </SheetClose>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  function InlineResp({ value, onChange }: { value?: string; onChange: (v?: string) => void }) {
    const [open, setOpen] = useState(false);
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className="inline-flex max-w-[220px] items-center gap-2 truncate rounded-md border border-input bg-background px-2 py-1 text-xs hover:bg-accent">
            <span className="truncate">{value || "Selecionar"}</span>
            <ChevronDown className="h-3 w-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-60 p-0">
          <Command>
            <CommandInput placeholder="Buscar responsável" />
            <CommandGroup>
              {[getCurrentUserName(), ...respOptions.map((o) => o.value)]
                .filter((v, i, arr) => arr.indexOf(v) === i)
                .map((r) => (
                <CommandItem key={r} onSelect={() => onChange(r)}>
                  {r}
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  function InlineDate({ value, onChange }: { value?: string; onChange: (v?: string) => void }) {
    const [open, setOpen] = useState(false);
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-2 py-1 text-xs hover:bg-accent">
            <CalendarIcon className="h-3 w-3" /> {value ? formatDate(value) : "Selecionar"}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0">
          <Calendar
            mode="single"
            selected={value ? new Date(value) : undefined}
            onSelect={(d) => onChange(d ? d.toISOString() : undefined)}
            locale={ptBR}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    );
  }

  // Header + Filters
  const header = (
    <header className="sticky top-0 z-10 space-y-4 bg-background/80 pb-4 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Obras</h1>
        <Button onClick={addObra}>
          <Plus className="mr-2 h-4 w-4" /> Adicionar
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xl">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, código, cliente…"
            className="pl-9"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <MultiSelect
          options={statusOptions}
          values={statusFilter}
          onChange={setStatusFilter}
          placeholder="Status"
          className="w-[220px]"
        />
        <Autocomplete
          value={clienteFilter}
          onChange={setClienteFilter}
          options={clientesOptions}
          placeholder="Cliente"
        />
        <MultiSelect
          options={respOptions}
          values={respFilter}
          onChange={setRespFilter}
          placeholder="Responsável"
          className="w-[240px]"
        />
        <DateRangePicker range={periodo} onChange={setPeriodo} />

        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" onClick={() => toast({ title: "Importar", description: "CSV/XLS (simulado)" })}>
            <Upload className="mr-2 h-4 w-4" /> Importar
          </Button>
          <Button variant="outline" onClick={() => toast({ title: "Exportar", description: "CSV/XLS (simulado)" })}>
            <Download className="mr-2 h-4 w-4" /> Exportar
          </Button>
        </div>
      </div>
    </header>
  );

  // Empty state
  if (!loading && filtered.length === 0) {
    return (
      <div className="p-6">
        {header}
        <div className="mt-10 grid place-items-center rounded-lg border border-dashed p-12 text-center">
          <div className="max-w-lg space-y-3">
            <p className="text-lg font-medium">Nenhuma obra encontrada</p>
            <p className="text-muted-foreground">Ajuste os filtros ou crie uma nova obra.</p>
            <Button onClick={addObra}>
              <Plus className="mr-2 h-4 w-4" /> Adicionar
            </Button>
          </div>
        </div>
        <WorkModal open={modalOpen} onOpenChange={setModalOpen} obra={editingObra} onSave={saveObra} />
      </div>
    );
  }

  return (
    <div className="p-6">
      {header}

      <section className="mt-4 overflow-x-auto rounded-lg border">
        <Table className="text-sm [&_th]:py-2 [&_th]:px-2 [&_td]:py-1.5 [&_td]:px-2 [&_th]:text-xs">
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[120px]">Código</TableHead>
              <TableHead className="min-w-[220px]">Nome da Obra</TableHead>
              <TableHead className="min-w-[200px]">Cliente</TableHead>
              <TableHead className="min-w-[180px]">Responsável</TableHead>
              <TableHead className="min-w-[180px]">Status</TableHead>
              <TableHead className="min-w-[140px]">Data Início</TableHead>
              <TableHead className="min-w-[160px]">Previsão Término</TableHead>
              <TableHead className="min-w-[140px]">Dias restantes</TableHead>
              <TableHead className="min-w-[140px]">Cidade/UF</TableHead>
              <TableHead className="w-[110px] text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading
              ? Array.from({ length: pageSize }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {Array.from({ length: 10 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : paginated.map((r) => {
                  const diasRestantes = r.previsaoTermino
                    ? differenceInCalendarDays(new Date(r.previsaoTermino), new Date())
                    : undefined;

                  return (
                    <TableRow
                      key={r.id}
                      className="cursor-pointer hover:bg-accent/50 h-10"
                      onClick={() => {
                        setEditingObra(r);
                        setModalOpen(true);
                      }}
                    >
                      <TableCell>{r.codigo}</TableCell>
                      <TableCell className="font-medium">{r.nome}</TableCell>
                      <TableCell>{r.cliente}</TableCell>
<TableCell>{r.responsavel || "—"}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()} className="whitespace-nowrap">
                        <InlineStatus
                          value={r.status}
                          onChange={(v) => {
                            cacheUpdateObra(r.id, { statusObra: statusToGlobal[v] });
                            setRows((prev) => prev.map((p) => (p.id === r.id ? { ...p, status: v } : p)));
                          }}
                        />
                      </TableCell>
<TableCell>{formatDate(r.dataInicio)}</TableCell>
<TableCell>{formatDate(r.previsaoTermino)}</TableCell>
                      <TableCell>
                        {diasRestantes !== undefined ? (
                          <span className={cn(
                            "rounded-full px-2 py-0.5 text-xs",
                            diasRestantes < 0 ? "bg-destructive/10 text-destructive" : diasRestantes <= 15 ? "bg-warning/10 text-warning" : "bg-success/10 text-success"
                          )}>
                            {diasRestantes < 0 ? `${Math.abs(diasRestantes)} dia(s) em atraso` : `${diasRestantes} dia(s)`}
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        {r.endereco?.cidade ? (
                          <span>
                            {r.endereco.cidade}/{r.endereco.uf || "—"}
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" aria-label="Editar" onClick={() => { setEditingObra(r); setModalOpen(true); }}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" aria-label="Arquivar" onClick={() => setConfirmArchive(r)}>
                            <Archive className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      </section>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="mt-4">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious onClick={() => setPage((p) => Math.max(1, p - 1))} />
              </PaginationItem>
              <span className="px-3 text-sm text-muted-foreground">
                Página {page} de {totalPages}
              </span>
              <PaginationItem>
                <PaginationNext onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {/* Modals */}
      <WorkModal open={modalOpen} onOpenChange={setModalOpen} obra={editingObra} onSave={saveObra} />

      <AlertDialog open={!!confirmArchive} onOpenChange={(o) => !o && setConfirmArchive(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Arquivar obra?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação poderá ser desfeita apenas reimportando os dados. Confirme para arquivar {confirmArchive?.nome}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmArchive) archiveObra(confirmArchive);
                setConfirmArchive(null);
              }}
            >
              Arquivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
